import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import ReactPaginate from "react-paginate";
import { ApiUrl } from "../../../ApiUrl";
import HostelEdit from "./HostelEdit";

const StudentHostelDetails = () => {
  const navigate = useNavigate();

  // Search Filters State
  const [studentName, setStudentName] = useState("");

  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);

  // Dropdown Options State
  const [hostelOptions, setHostelOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);

  // Table Data & Pagination State
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentHostelDetails, setStudentHostelDetails] = useState(null);

  // Load initial dropdown values
  useEffect(() => {
    // Hostels
    const fetchHostels = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelMasterList/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.hostel_name,
          }));
          setHostelOptions(options);
        }
      } catch (error) {
        console.error("Error fetching hostels:", error);
      }
    };

    // Room Types
    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomTypeList/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.type_name,
          }));
          setRoomTypeOptions(options);
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    };

    fetchHostels();
    fetchRoomTypes();
  }, []);

  // Fetch blocks based on selected Hostel
  useEffect(() => {
    if (!selectedHostel) {
      setBlockOptions([]);
      setSelectedBlock(null);
      return;
    }
    const fetchBlocks = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelBlockList/${selectedHostel.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.block_name,
          }));
          setBlockOptions(options);
        }
      } catch (error) {
        console.error("Error fetching blocks:", error);
      }
    };
    fetchBlocks();
  }, [selectedHostel]);

  // Fetch floors based on selected Block
  useEffect(() => {
    if (!selectedBlock) {
      setFloorOptions([]);
      setSelectedFloor(null);
      return;
    }
    const fetchFloors = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelFloorList/${selectedBlock.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.floor_name,
          }));
          setFloorOptions(options);
        }
      } catch (error) {
        console.error("Error fetching floors:", error);
      }
    };
    fetchFloors();
  }, [selectedBlock]);

  // Fetch rooms based on selected Floor and Room Type
  useEffect(() => {
    if (!selectedFloor || !selectedRoomType) {
      setRoomOptions([]);
      setSelectedRoom(null);
      return;
    }
    const fetchRooms = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomList/${selectedFloor.value}/${selectedRoomType.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.room_no,
          }));
          setRoomOptions(options);
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    };
    fetchRooms();
  }, [selectedFloor, selectedRoomType]);

  // Fetch beds based on selected Room
  useEffect(() => {
    if (!selectedRoom) {
      setBedOptions([]);
      setSelectedBed(null);
      return;
    }
    const fetchBeds = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelBedList/${selectedRoom.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          const options = data.data.map((item) => ({
            value: item.id,
            label: item.bed_fee_amount
              ? `${item.bed_no} - Fee ${item.bed_fee_amount}`
              : item.bed_no,
          }));
          setBedOptions(options);
        }
      } catch (error) {
        console.error("Error fetching beds:", error);
      }
    };
    fetchBeds();
  }, [selectedRoom]);

  const handleSearch = async (studentNameOverride) => {
    try {
      // React passes a click event to handlers used directly as onClick
      // callbacks. Only use an explicit string override; otherwise search
      // with the current input value.
      const searchText =
        typeof studentNameOverride === "string"
          ? studentNameOverride.trim()
          : typeof studentName === "string"
            ? studentName.trim()
            : "";
      const queryParams = new URLSearchParams();
      if (searchText) {
        queryParams.append("student_name", searchText);
      }
      const activeSessionId = localStorage.getItem("academicSessionId");
      if (activeSessionId) queryParams.append("session_id", activeSessionId);
      if (selectedHostel) queryParams.append("hostel_id", selectedHostel.value);
      if (selectedBlock) queryParams.append("block_id", selectedBlock.value);
      if (selectedFloor) queryParams.append("floor_id", selectedFloor.value);
      if (selectedRoomType) queryParams.append("room_type_id", selectedRoomType.value);
      if (selectedRoom) queryParams.append("room_id", selectedRoom.value);
      if (selectedBed) queryParams.append("bed_id", selectedBed.value);

      const response = await fetch(`${ApiUrl.apiurl}HOSTEL/StudentHostelAssignmentList/?${queryParams.toString()}`);
      const data = await response.json();
      if (data.message === "success") {
        setTableData(data.data);
      } else {
        setTableData([]);
      }
      setCurrentPage(0);
    } catch (error) {
      console.error("Error searching assignments:", error);
      setTableData([]);
    }
  };

  // Filter by student name as the user types, with a small debounce.
  useEffect(() => {
    if (!studentName.trim()) return undefined;
    const timer = setTimeout(() => handleSearch(studentName), 350);
    return () => clearTimeout(timer);
    // handleSearch intentionally uses the latest filter state from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentName]);

  const handleEdit = async (student) => {
    setSelectedStudent({
      student_id: student.student_id || student.student,
      studentname: student.student_name,
      barcode: student.barcode,
      assignmentData: student,
    });
    setStudentHostelDetails(null);
    setShowEditModal(true);

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}HOSTEL/HostelDetailsRetereiveByStudent/${student.student_id || student.student}`
      );
      const data = await response.json();
      setStudentHostelDetails(data.message === "success" ? data.data : null);
    } catch (error) {
      console.error("Error fetching hostel details:", error);
      setStudentHostelDetails(null);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedStudent(null);
    setStudentHostelDetails(null);
  };

  const handleClear = () => {
    setStudentName("");
    setSelectedHostel(null);
    setSelectedBlock(null);
    setSelectedFloor(null);
    setSelectedRoomType(null);
    setSelectedRoom(null);
    setSelectedBed(null);
    setTableData([]);
    setCurrentPage(0);
  };

  const handleClose = () => {
    navigate("/admin/dashboard");
  };

  // Pagination calculations
  const offset = currentPage * itemsPerPage;
  const currentData = tableData.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(tableData.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const filterPanelStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #dee2e6",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  };

  const tableHeaderStyle = {
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  };

  return (
    <div className="container-fluid mt-3">
      <div className="row">
        <div className="col-12">
          <div className="card p-0 shadow-sm border-0">
            <div className="card-body p-3">
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#000000",
                }}
              >
                STUDENT HOSTEL DETAILS
              </p>

              {/* Action Buttons Row */}
              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "120px" }}
                    onClick={() => navigate("/admin/student-hostel-details-create")}
                  >
                    New
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "120px" }}
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: "120px" }}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ width: "120px" }}
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Filters Container */}
              <div className="row mx-0 mb-4 p-3 rounded" style={filterPanelStyle}>
                {/* Row 1 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Student Name</label>
                  <div className="d-flex">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter student name"
                      value={studentName}
                      onChange={(event) => setStudentName(event.target.value)}
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Hostel Name</label>
                  <Select
                    options={hostelOptions}
                    placeholder="Select Hostel"
                    value={selectedHostel}
                    onChange={setSelectedHostel}
                    isClearable
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Hostel Block</label>
                  <Select
                    options={blockOptions}
                    placeholder="Select Block"
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    isClearable
                    disabled={!selectedHostel}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Floor No</label>
                  <Select
                    options={floorOptions}
                    placeholder="Select Floor"
                    value={selectedFloor}
                    onChange={setSelectedFloor}
                    isClearable
                    disabled={!selectedBlock}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Room Type</label>
                  <Select
                    options={roomTypeOptions}
                    placeholder="Select Room Type"
                    value={selectedRoomType}
                    onChange={setSelectedRoomType}
                    isClearable
                  />
                </div>

                {/* Row 3 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Room No</label>
                  <Select
                    options={roomOptions}
                    placeholder="Select Room"
                    value={selectedRoom}
                    onChange={setSelectedRoom}
                    isClearable
                    disabled={!selectedFloor || !selectedRoomType}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Bed No</label>
                  <Select
                    options={bedOptions}
                    placeholder="Select Bed No"
                    value={selectedBed}
                    onChange={setSelectedBed}
                    isClearable
                    disabled={!selectedRoom}
                  />
                </div>
              </div>

              {/* Search Results Table */}
              <div className="col-12 px-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-primary text-center">
                      <tr>
                        <th style={tableHeaderStyle}>Sr.No</th>
                        <th style={tableHeaderStyle}>Student Name</th>
                        <th style={tableHeaderStyle}>ONMRC Registration No</th>
                        <th style={tableHeaderStyle}>Admission No</th>
                        <th style={tableHeaderStyle}>Roll no</th>
                        <th style={tableHeaderStyle}>Session</th>
                        <th style={tableHeaderStyle}>Class</th>
                        <th style={tableHeaderStyle}>Section</th>
                        <th style={tableHeaderStyle}>Hostel Name</th>
                        <th style={tableHeaderStyle}>Block</th>
                        <th style={tableHeaderStyle}>Floor No</th>
                        <th style={tableHeaderStyle}>Room Type</th>
                        <th style={tableHeaderStyle}>Room No</th>
                        <th style={tableHeaderStyle}>Bed No</th>
                        <th style={tableHeaderStyle}>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.length > 0 ? (
                        currentData.map((item, index) => (
                          <tr key={item.id}>
                            <td className="text-center">{offset + index + 1}</td>
                            <td>{item.student_name}</td>
                            <td>{item.onmrc_registration_no || "N/A"}</td>
                            <td>{item.admission_no || "N/A"}</td>
                            <td>{item.roll_no || "N/A"}</td>
                            <td>{item.session_code}</td>
                            <td>{item.course_name}</td>
                            <td>{item.section_name}</td>
                            <td>{item.hostel_name}</td>
                            <td>{item.block_name}</td>
                            <td>{item.floor_name}</td>
                            <td>{item.room_type_name}</td>
                            <td>{item.room_name}</td>
                            <td>{item.bed_name}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link text-primary text-decoration-none p-0"
                                onClick={() => handleEdit(item)}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="15" className="text-center py-3">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {tableData.length > itemsPerPage && (
                    <ReactPaginate
                      previousLabel={"Previous"}
                      nextLabel={"Next"}
                      breakLabel={"..."}
                      breakClassName={"page-item"}
                      breakLinkClassName={"page-link"}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={handlePageClick}
                      containerClassName={"pagination justify-content-center mt-3"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextClassName={"page-item"}
                      nextLinkClassName={"page-link"}
                      activeClassName={"active"}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HostelEdit
        show={showEditModal}
        handleClose={closeEditModal}
        studentData={selectedStudent}
        transportDetails={studentHostelDetails}
        routes={[]}
        assignmentData={selectedStudent?.assignmentData}
        onSaveSuccess={handleSearch}
      />

    </div>
  );
};

export default StudentHostelDetails;
